import { diaNegocio, inicioSemana, REPORTES, sumarDias, type ClaveReporte, type Reporte } from '@barbones/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { BarChart3, FileDown, FileSpreadsheet, Table2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { api, descargar, mensajeError } from '@/api/cliente';
import { useSesion } from '@/api/sesion';
import { EncabezadoPagina, ModuleLayout } from '@/components/layout/ModuleLayout';
import { Button } from '@/components/ui/button';
import { Esqueleto, Pestana, PestanaContenido, Pestanas, PestanasLista, Tarjeta, Vacio } from '@/components/ui/data';
import { Campo, Input, Select } from '@/components/ui/form';
import { PanelLateral } from '@/components/ui/overlay';
import { cn } from '@/lib/utils';
import { useBarberos, useServicios } from '../agenda/datos';
import { DetalleVenta, useVentaDetalle } from '../ventas/DetalleVenta';
import { DetalleArqueo, useArqueoDetalle } from './DetalleArqueo';
import { GraficaReporte } from './Graficas';
import { TablaReporte } from './TablaReporte';
import { avisarError } from '@/lib/avisos';

const GRUPOS = ['Ventas', 'Caja', 'Personal', 'Clientes', 'Inventario'] as const;

function rangos(hoy: string) {
  const lunes = inicioSemana(hoy, 1);
  const mes = `${hoy.slice(0, 8)}01`;
  const mesPasadoFin = sumarDias(mes, -1);
  return [
    { etiqueta: 'Hoy', desde: hoy, hasta: hoy },
    { etiqueta: 'Esta semana', desde: lunes, hasta: sumarDias(lunes, 6) },
    { etiqueta: 'Semana pasada', desde: sumarDias(lunes, -7), hasta: sumarDias(lunes, -1) },
    { etiqueta: 'Este mes', desde: mes, hasta: hoy },
    { etiqueta: 'Mes pasado', desde: `${mesPasadoFin.slice(0, 8)}01`, hasta: mesPasadoFin },
  ];
}

export function ReportesPage() {
  const { usuario } = useSesion();
  const permisos = usuario?.permisos ?? [];
  const hoy = diaNegocio(new Date());
  const disponibles = useMemo(
    () => (Object.keys(REPORTES) as ClaveReporte[]).filter((k) => (REPORTES[k].permisos as readonly string[]).some((p) => permisos.includes(p as never))),
    [permisos],
  );
  const soloPropios = !permisos.includes('reportes.dueno') && !permisos.includes('reportes.recepcion');

  // El reporte elegido vive en la URL (?r=…): se puede abrir directo desde el buscador o compartir.
  const navegar = useNavigate();
  const { r } = useSearch({ strict: false }) as { r?: string };
  const clave: ClaveReporte | undefined = r && (disponibles as string[]).includes(r) ? (r as ClaveReporte) : disponibles[0];
  const setClave = (k: ClaveReporte) => void navegar({ to: '/reportes', search: { r: k } });
  const [f, setF] = useState({ desde: hoy, hasta: hoy, barberoId: '', servicioId: '', agrupar: 'dia' });
  const def = clave ? REPORTES[clave] : undefined;
  const filtrosDef = (def?.filtros ?? []) as readonly string[];
  const barberos = useBarberos();
  const servicios = useServicios(false);

  const query = {
    desde: f.desde,
    hasta: f.hasta,
    barberoId: filtrosDef.includes('barbero') && !soloPropios ? f.barberoId || undefined : undefined,
    servicioId: filtrosDef.includes('servicio') ? f.servicioId || undefined : undefined,
    agrupar: filtrosDef.includes('agrupar') ? f.agrupar : undefined,
  };
  const reporte = useQuery({
    queryKey: ['reporte', clave, query],
    queryFn: () => api<Reporte>(`/reportes/${clave}`, { query }),
    enabled: !!clave && f.desde <= f.hasta,
    placeholderData: keepPreviousData,
  });

  // En «Ventas e ingresos» cada fila abre el detalle de su venta (pagos, propina, quién cobró).
  const abreVentas = clave === 'r5-ventas-dia' && permisos.includes('ventas.ver_detalle');
  const [ventaAbierta, setVentaAbierta] = useState<string | null>(null);
  const detalle = useVentaDetalle(abreVentas ? ventaAbierta : null);
  // En cierres y cortes cada fila abre el conteo con todas las ventas de su turno.
  const abreArqueos = (clave === 'r18-cierres-caja' || clave === 'r19-cortes-caja') && permisos.includes('reportes.dueno');
  const [arqueoAbierto, setArqueoAbierto] = useState<string | null>(null);
  const arqueo = useArqueoDetalle(abreArqueos ? arqueoAbierto : null);

  const bajar = (formato: 'xlsx' | 'pdf') =>
    descargar(`/reportes/${clave}`, { ...query, formato }, `${clave}_${f.desde}_${f.hasta}.${formato}`).catch((e) => avisarError(e));

  if (!clave || !def) {
    return (
      <ModuleLayout titulo="Reportes">
        <Vacio icono={<BarChart3 />} titulo="No tienes reportes disponibles" />
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout titulo="Reportes">
      <EncabezadoPagina titulo="Reportes" descripcion="Consulta, filtra y descarga en Excel o PDF." />
      <div className="grid gap-5 lg:grid-cols-[260px_1fr]">
        {/* En pantallas angostas el catálogo es un selector para que el reporte quede a la vista. */}
        <div className="lg:hidden">
          <Campo etiqueta="Reporte">
            {(p) => (
              <Select {...p} value={clave} onChange={(e) => setClave(e.target.value as ClaveReporte)}>
                {GRUPOS.map((g) => {
                  const del = disponibles.filter((k) => REPORTES[k].grupo === g);
                  return del.length ? (
                    <optgroup key={g} label={g}>
                      {del.map((k) => (
                        <option key={k} value={k}>
                          {REPORTES[k].titulo}
                        </option>
                      ))}
                    </optgroup>
                  ) : null;
                })}
              </Select>
            )}
          </Campo>
        </div>
        <nav aria-label="Catálogo de reportes" className="hidden content-start gap-4 lg:grid">
          {GRUPOS.map((g) => {
            const del = disponibles.filter((k) => REPORTES[k].grupo === g);
            if (!del.length) return null;
            return (
              <div key={g}>
                <p className="mb-1.5 px-2 text-[11.5px] font-semibold uppercase tracking-wider text-foreground-muted">{g}</p>
                <ul className="grid gap-0.5">
                  {del.map((k) => (
                    <li key={k}>
                      <button
                        type="button"
                        onClick={() => setClave(k)}
                        aria-current={k === clave ? 'page' : undefined}
                        className={cn(
                          'w-full cursor-pointer rounded-md px-2 py-1.5 text-left text-[14px] transition-colors hover:bg-muted focus-visible:outline-2 focus-visible:outline-accent',
                          k === clave && 'bg-primary text-on-primary hover:bg-primary',
                        )}
                      >
                        {REPORTES[k].titulo}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>

        <section className="min-w-0">
          <div className="mb-3">
            <h2 className="font-display text-[24px] leading-tight">{def.titulo}</h2>
            <p className="text-[14px] text-foreground-muted">{def.descripcion}</p>
          </div>

          <Tarjeta className="mb-4 p-3">
            <div className="flex flex-wrap items-end gap-2">
              <Campo etiqueta="Desde">{(p) => <Input {...p} type="date" value={f.desde} max={f.hasta} onChange={(e) => setF({ ...f, desde: e.target.value })} />}</Campo>
              <Campo etiqueta="Hasta" error={f.desde > f.hasta ? 'Debe ser posterior a «Desde»' : undefined}>
                {(p) => <Input {...p} type="date" value={f.hasta} min={f.desde} onChange={(e) => setF({ ...f, hasta: e.target.value })} />}
              </Campo>
              {filtrosDef.includes('barbero') && !soloPropios && (
                <Campo etiqueta="Barbero">
                  {(p) => (
                    <Select {...p} value={f.barberoId} onChange={(e) => setF({ ...f, barberoId: e.target.value })}>
                      <option value="">Todos</option>
                      {barberos.data?.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.nombre}
                        </option>
                      ))}
                    </Select>
                  )}
                </Campo>
              )}
              {filtrosDef.includes('servicio') && (
                <Campo etiqueta="Servicio">
                  {(p) => (
                    <Select {...p} value={f.servicioId} onChange={(e) => setF({ ...f, servicioId: e.target.value })}>
                      <option value="">Todos</option>
                      {servicios.data?.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.nombre}
                        </option>
                      ))}
                    </Select>
                  )}
                </Campo>
              )}
              {filtrosDef.includes('agrupar') && (
                <Campo etiqueta="Agrupar por">
                  {(p) => (
                    <Select {...p} value={f.agrupar} onChange={(e) => setF({ ...f, agrupar: e.target.value })}>
                      <option value="dia">Día</option>
                      <option value="semana">Semana</option>
                      <option value="mes">Mes</option>
                    </Select>
                  )}
                </Campo>
              )}
              <div className="ml-auto flex gap-2">
                <Button variante="secundario" onClick={() => void bajar('xlsx')} disabled={f.desde > f.hasta}>
                  <FileSpreadsheet /> Excel
                </Button>
                <Button variante="secundario" onClick={() => void bajar('pdf')} disabled={f.desde > f.hasta}>
                  <FileDown /> PDF
                </Button>
              </div>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {rangos(hoy).map((r) => {
                const activo = r.desde === f.desde && r.hasta === f.hasta;
                return (
                  <button
                    key={r.etiqueta}
                    type="button"
                    onClick={() => setF({ ...f, desde: r.desde, hasta: r.hasta })}
                    aria-pressed={activo}
                    className={cn(
                      'cursor-pointer rounded-full border border-border px-2.5 py-0.5 text-[12.5px] transition-colors hover:border-accent',
                      activo && 'border-accent bg-accent-soft text-accent',
                    )}
                  >
                    {r.etiqueta}
                  </button>
                );
              })}
            </div>
          </Tarjeta>

          {reporte.isLoading ? (
            <Esqueleto className="h-64" />
          ) : reporte.isError ? (
            <Tarjeta>
              <Vacio icono={<BarChart3 />} titulo="No se pudo generar el reporte" descripcion={mensajeError(reporte.error)} />
            </Tarjeta>
          ) : reporte.data ? (
            <Pestanas defaultValue="tabla" key={clave}>
              {reporte.data.grafica && (
                <PestanasLista>
                  <Pestana value="tabla" className="inline-flex items-center gap-1.5">
                    <Table2 className="size-4" /> Tabla
                  </Pestana>
                  <Pestana value="grafica" className="inline-flex items-center gap-1.5">
                    <BarChart3 className="size-4" /> Gráfica
                  </Pestana>
                </PestanasLista>
              )}
              <PestanaContenido value="tabla">
                <Tarjeta className={cn('mt-3 overflow-x-auto transition-opacity', reporte.isFetching && 'opacity-60')}>
                  {reporte.data.filas.length ? (
                    <>
                      {abreVentas && <p className="px-3 pt-2.5 text-[12.5px] text-foreground-muted">Toca una venta para ver su detalle.</p>}
                      {abreArqueos && (
                        <p className="px-3 pt-2.5 text-[12.5px] text-foreground-muted">
                          Toca un {clave === 'r18-cierres-caja' ? 'cierre' : 'corte'} para ver todas sus ventas.
                        </p>
                      )}
                      <TablaReporte
                        reporte={reporte.data}
                        {...(abreVentas && {
                          alAbrirFila: (fila) => setVentaAbierta(String(fila.ventaId)),
                          etiquetaFila: (fila) => `Ver detalle del folio ${fila.folio}`,
                        })}
                        {...(abreArqueos && {
                          alAbrirFila: (fila) => setArqueoAbierto(String(fila.arqueoId)),
                          etiquetaFila: (fila) => `Ver ventas del ${clave === 'r18-cierres-caja' ? 'cierre' : 'corte'} de ${fila.responsable}`,
                        })}
                      />
                    </>
                  ) : (
                    <Vacio icono={<Table2 />} titulo="Sin datos en el periodo" descripcion="Prueba con otro rango de fechas." />
                  )}
                </Tarjeta>
              </PestanaContenido>
              {reporte.data.grafica && (
                <PestanaContenido value="grafica">
                  <Tarjeta className="mt-3 p-5">
                    {reporte.data.filas.length ? <GraficaReporte reporte={reporte.data} /> : <Vacio icono={<BarChart3 />} titulo="Sin datos en el periodo" />}
                  </Tarjeta>
                </PestanaContenido>
              )}
            </Pestanas>
          ) : null}
        </section>
      </div>
      <PanelLateral abierto={abreVentas && !!ventaAbierta} onCambiarAbierto={(a) => !a && setVentaAbierta(null)} titulo="Detalle de la venta">
        {detalle.data ? (
          <DetalleVenta venta={detalle.data} />
        ) : detalle.isError ? (
          <p className="text-[13px] text-destructive">{mensajeError(detalle.error)}</p>
        ) : (
          <Esqueleto className="h-64" />
        )}
      </PanelLateral>
      <PanelLateral
        abierto={abreArqueos && !!arqueoAbierto}
        onCambiarAbierto={(a) => !a && setArqueoAbierto(null)}
        titulo={clave === 'r19-cortes-caja' ? 'Detalle del corte' : 'Detalle del cierre'}
      >
        {arqueo.data ? (
          <DetalleArqueo arqueo={arqueo.data} />
        ) : arqueo.isError ? (
          <p className="text-[13px] text-destructive">{mensajeError(arqueo.error)}</p>
        ) : (
          <Esqueleto className="h-64" />
        )}
      </PanelLateral>
    </ModuleLayout>
  );
}
